import React from 'react';
import { Phone, MessageCircle, MapPin, Clock, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/api';

const ContactSection = () => {
  const handleCall = () => {
    window.location.href = `tel:${companyInfo.phoneClean}`;
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(companyInfo.whatsappMessage);
    window.open(`https://wa.me/${companyInfo.phoneClean.replace('+', '')}?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-zinc-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-amber-400 text-sm font-semibold tracking-wider uppercase mb-4">
            İletişim
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Bize <span className="text-amber-400">Ulaşın</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Denizli taksi hizmeti için 7/24 bize ulaşabilirsiniz. 
            Hemen arayın veya WhatsApp'tan mesaj gönderin.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            {/* Phone Card */}
            <div 
              onClick={handleCall}
              className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl p-6 border border-zinc-700/50 hover:border-amber-500/50 transition-all duration-300 cursor-pointer group hover:shadow-xl hover:shadow-amber-500/10"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-zinc-900" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Telefon</p>
                  <p className="text-xl font-bold text-white group-hover:text-amber-400 transition-colors">
                    {companyInfo.phone}
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Card */}
            <div 
              onClick={handleWhatsApp}
              className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl p-6 border border-zinc-700/50 hover:border-green-500/50 transition-all duration-300 cursor-pointer group hover:shadow-xl hover:shadow-green-500/10"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm mb-1">WhatsApp</p>
                  <p className="text-xl font-bold text-white group-hover:text-green-400 transition-colors">
                    Mesaj Gönder
                  </p>
                </div>
              </div>
            </div>

            {/* Location Card */}
            <div className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl p-6 border border-zinc-700/50">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-zinc-700 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Konum</p>
                  <p className="text-lg font-semibold text-white">Denizli, Türkiye</p>
                </div>
              </div>
            </div>

            {/* Hours Card */}
            <div className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl p-6 border border-zinc-700/50">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-zinc-700 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Çalışma Saatleri</p>
                  <p className="text-lg font-semibold text-white">7/24 Açık</p>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl p-2 border border-zinc-700/50 h-full min-h-[400px]">
              <iframe
                title="Denizli Konum"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d100073.02399738756!2d29.0226537!3d37.7833333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c73fb35f75f395%3A0x88c4dd21c5e0ba6a!2sDenizli%2C%20T%C3%BCrkiye!5e0!3m2!1str!2s!4v1699999999999!5m2!1str!2s"
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '1rem', minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4 items-center bg-zinc-800/50 rounded-2xl p-6 border border-zinc-700/50">
            <p className="text-zinc-300 text-lg">
              Hemen taksi çağırmak için:
            </p>
            <Button
              onClick={handleCall}
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold px-8 shadow-lg shadow-amber-500/30 transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              {companyInfo.phone}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
